import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Page, FocusDiv } from '@accedo/vdkweb-tv-ui';
import cx from 'classnames';

import Logo from '#/static/images/icons/logo-login.svg';
import { InfoForm, InfoInput } from '#/widgets/forms';
import { FocusButton, Footer } from '#/widgets/ui';
import { ErrorDispatcher } from '#/components';

import { authFlowRoutes } from '#/utils/routes';

import { signInPageTitle } from '#/config/strings';
import { authScreenNames } from '#/config/constants';
import { LOGIN, login } from '#/redux/modules/identity/actions';
import { toggleSpinner } from '#/redux/modules/spinner/actions';
import { sendScreenView } from '#/redux/modules/firestore/actions';
import { getSensicalTvConfig } from '#/redux/modules/accedoOne/selectors';
import { SIGNIN } from '#/utils/navigationHelper';

import { isWorkstation } from '#/utils/componentStyleConfig';
import styles from './SignIn.scss';
import authStyles from '../authflow.scss';
import LoginRedirect from '../LoginRedirect/LoginRedirect';
import TVSignIn from '../TVSignIn/TVSignIn';

const SignIn = () => {
  const dispatch = useDispatch();
  const { logoUri = Logo } =
    useSelector(state => getSensicalTvConfig(state)) || {};
  const [loginFields, setLoginFields] = useState({ email: '', credential: '' });

  useEffect(() => {
    dispatch(sendScreenView(authScreenNames.SIGN_IN));
  }, []);

  const handleLogin = useCallback(async () => {
    try {
      dispatch(toggleSpinner(true));
      await dispatch(login(loginFields));
    } finally {
      dispatch(toggleSpinner(false));
    }
  }, [loginFields]);

  const webComponent = (
    <Page
      className={cx(authStyles.authFlow, styles.signIn)}
      autofocus
      nav={{ id: SIGNIN.PAGE, forwardFocus: SIGNIN.CONTAINER }}
    >
      <ErrorDispatcher errorId={LOGIN} />
      <LoginRedirect />
      <FocusDiv
        className={authStyles.content}
        nav={{ id: SIGNIN.CONTAINER, forwardFocus: SIGNIN.EMAIL_INPUT }}
      >
        <img src={logoUri} alt="Logo" />
        <h1 className={styles.signInHeading}>{signInPageTitle}</h1>
        <InfoForm onChange={(data, isValid) => isValid && setLoginFields(data)}>
          <InfoInput
            fieldName="email"
            fieldLabel="Email address"
            inputAttributes={{ type: 'email' }}
            nav={{
              id: SIGNIN.EMAIL_INPUT,
              nextdown: SIGNIN.PASSWORD_INPUT,
              parent: SIGNIN.CONTAINER,
            }}
          />
          <InfoInput
            fieldName="credential"
            fieldLabel="Enter Password"
            inputAttributes={{ type: 'password' }}
            nav={{
              id: SIGNIN.PASSWORD_INPUT,
              nextup: SIGNIN.EMAIL_INPUT,
              nextdown: SIGNIN.SIGNIN_BUTTON,
              parent: SIGNIN.CONTAINER,
            }}
          />
          <span className={styles.forgotPasswordLink}>
            <Link to={authFlowRoutes.FORGOT_PASSWORD}>Forgot password?</Link>
          </span>
        </InfoForm>
        <FocusButton
          onClick={handleLogin}
          primary
          nav={{
            id: SIGNIN.SIGNIN_BUTTON,
            nextup: SIGNIN.PASSWORD_INPUT,
            parent: SIGNIN.CONTAINER,
          }}
        >
          Sign In
        </FocusButton>
      </FocusDiv>
      <Footer />
    </Page>
  );

  return isWorkstation() ? webComponent : <TVSignIn />;
};

export default SignIn;
